<template>
  <div class="add-to-cart-wrapper">
    <CustomButton
      :is-small="isSmall"
      :aria-attrs="{
        'aria-label': `Add Items to Cart`,
        'aria-disabled': disabled,
      }"
      :disabled="disabled"
      style-color="black"
      @click-button="addToCartAction()"
    >
      {{
        disabled && !rootStore?.siteUnderMaintenance
          ? noItemsSelected
            ? 'Select Products'
            : 'Adding...'
          : addToCartMsg
      }}
    </CustomButton>
  </div>
</template>

<script setup lang="ts">
import { ProductV2 } from '@solo-stove/types/contentstack';
import { AddToCartItem } from '~/types/bigcommerce';
import { useCloudinary } from '~/composables/cloudinary';
import { useCartStore } from '~/store/cart';
import { useRootStore } from '~/store';

const cartStore = useCartStore();
const rootStore = useRootStore();
const cloudinary = useCloudinary();

const emit = defineEmits(['added-to-cart']);

const props = defineProps({
  products: {
    type: Array as () => ProductV2[],
    required: true,
  },
  selectedOptionIds: {
    type: Array as PropType<any>,
    required: false,
    default: () => [],
  },
  addToCartMsg: {
    type: String,
    default: 'Add Items to Cart',
    required: false,
  },
  isSmall: {
    type: Boolean,
    default: false,
  },
});

const state = reactive({
  buttonClicked: false,
});

const cartActionInProgress = computed(() => cartStore.cartActionInProgress);
// const gettingCart = computed(() => cartStore.gettingCart);
const noItemsSelected = computed<boolean>(() => props.products?.length === 0);
const disabled = computed<boolean>(
  () => rootStore?.siteUnderMaintenance || noItemsSelected.value || (cartActionInProgress.value && state.buttonClicked)
);

async function addToCartAction() {
  state.buttonClicked = true;
  const cartProducts: AddToCartItem[] = props?.products?.map((item) => {
    //   const gaPageViewProduct = [
    //   {
    //     product: {
    //       id: baseProduct.value?.id,
    //       sku: sku.value,
    //       parentSku: parentSku.value || undefined,
    //       name: productName.value,
    //       brand: rootStore.brandSettings?.brandName,
    //       image: primaryImage.value,
    //       price: currentPrice.value,
    //       originalPrice: originalPrice.value,
    //       category: state.product?.product_taxonomy?.category?.join(', '),
    //     },
    //     ...(selectedVariantBcVariant.value && { variant: selectedVariantBcVariant.value }),
    //     hasVariants: hasVariants.value,
    //     quantity: 1,
    //   },
    // ];
    const productOfferings = item?.product_offerings;
    const baseProduct = productOfferings?.bc_primary_product?.product;
    const isVariant = !!productOfferings?.product_variations?.length;
    let variant = null;
    if (isVariant) {
      const found = productOfferings?.product_variations?.find(
        (variation: any) => variation?.bc_variant?.id === props.selectedOptionIds[item?.tileIndex]
      );
      variant = found;
    }
    const imgFallback = productOfferings?.base_image_set?.[0]?.public_id || undefined;
    const imgPrimary = variant?.image?.[0]?.public_id || imgFallback;
    const image = imgPrimary ? cloudinary.generateImageUrl(imgPrimary) : '';
    const sku = isVariant && variant?.bc_variant?.sku ? variant.bc_variant.sku : baseProduct.sku;
    const parentSku = isVariant ? baseProduct.sku : undefined;
    const variantId = isVariant ? variant?.bc_variant?.id : undefined;
    return {
      quantity: (item?.min_quantity as number) || 1,
      product: {
        id: baseProduct.id,
        variantId,
        sku,
        parentSku,
        name: productOfferings.product_title,
        image,
        brand: productOfferings?.brand?.brand_name,
      },
      hasVariants: isVariant,
    };
  });
  await useAddToCart({ cartItems: cartProducts });
  emit('added-to-cart', cartProducts);
  state.buttonClicked = false;
}
</script>

<style lang="scss" scoped>
.add-to-cart-wrapper {
  width: 100%;
}
:deep(.btn) {
  width: 100%;
}
#a2c-button :deep(.new-cta.btn) {
  font-size: 1.125rem;
  background-color: $color-neutral-cool-900;
  border-color: $color-neutral-cool-900;
  text-transform: none;
}
</style>
